import RoleTypes from "@wesstron/utils/Api/constants/roleTypes";
import UserTypes from "@wesstron/utils/Api/constants/userTypes";
import {get, memoize} from "lodash";
import {createSelector} from "reselect";
import {extractDevPlcmnts} from "../utils/DeviceLocationUtils";
import {userIsService} from "../utils/NewRolesUtils2";
import {displayComponent} from "../utils/WithRolesUtils";
import {getManageBuildingsList} from "./buildingsSelector";

const getUser = (state) => state.user.user;

const getFarm = (state, props) => props?.overrideFarmID ?? state.location.farm;

const getSpecificRoles = (state, props) => props.roles;

export const hasAccessByRoles = createSelector(
    [getUser, getSpecificRoles, getFarm],
    (user, roles, farmID) => {
        if ([UserTypes.SERVICE, UserTypes.OWNER].includes(user.UserType))
            return true;
        let hasAccess = false;
        for (const {Role, LocalRights} of get(user, "Roles", [])) {
            if (
                roles.includes(Role) &&
                LocalRights.some(({FarmID}) => FarmID === farmID)
            ) {
                hasAccess = true;
            }
        }
        return hasAccess;
    }
);

export const roleSelector = createSelector(
    [getFarm, getUser, getManageBuildingsList],
    (_farm, _user, _buildingsList) => {
        const isService = userIsService(_user, _farm);
        const displayComponentByDevice = memoize(
            ({roles = [], device = null, index = null} = {}) => {
                return displayComponent(
                    roles,
                    device ? extractDevPlcmnts(device, index) : null,
                    _farm,
                    _user,
                    _buildingsList,
                    isService,
                    {},
                    []
                );
            },
            (o) =>
                `${JSON.stringify(o.roles)}_${o.device?.DevID || null}_${o.device?.DtaModTime || null}_${o.index || null}`
        );
        const displayComponentByPlacementID = memoize(
            ({roles = [], placementId = null} = {}) => {
                return displayComponent(
                    roles,
                    [placementId],
                    _farm,
                    _user,
                    _buildingsList,
                    isService,
                    false,
                    {},
                    []
                );
            },
            (o) => `${JSON.stringify(o.roles)}_${o?.placementId || null}`
        );
        const hasAnyRole = ({roles = [], device = null, index = null} = {}) =>
            roles.some((role) =>
                displayComponentByDevice({
                    roles: [role],
                    device,
                    index,
                })
            );
        const hasAnyRoleByPlacementID = ({
            roles = [],
            placementId = null,
        } = {}) =>
            roles.some((role) =>
                displayComponentByPlacementID({
                    roles: [role],
                    placementId,
                })
            );
        return {
            hasRole: displayComponentByDevice,
            hasRoleByPlacementID: displayComponentByPlacementID,
            hasAnyRole,
            hasAnyRoleByPlacementID,
            isService,
        };
    }
);

export const isReadOnly = (state, role, device, index = null) => {
    return !roleSelector(state).hasRole({roles: [role], device, index});
};

export const isReadOnlyByPlacementID = (state, role, placementId) => {
    return !roleSelector(state).hasRoleByPlacementID({
        roles: [role],
        placementId,
    });
};

const getExpectedRole = (_, role) => role;

const getDevices = (_, __, devices) => devices;

export const makeIsMultiDeviceReadOnly = () =>
    createSelector(
        getUser,
        getExpectedRole,
        getDevices,
        getFarm,
        getManageBuildingsList,
        (user, role, devices, farm, buildingsList) => {
            const isService = userIsService(user, farm);
            for (let device of devices) {
                if (
                    !displayComponent(
                        [role],
                        extractDevPlcmnts(device),
                        farm,
                        user,
                        buildingsList,
                        isService,
                        false,
                        {},
                        []
                    )
                )
                    return true;
            }
            return false;
        }
    );

export const hasAIRole = createSelector([getUser, getFarm], (user, farm) => {
    if (user.UserType === UserTypes.SERVICE) return true;
    if (!farm) {
        return get(user, "Roles", []).find((r) => r.Role === RoleTypes.AI_READ);
    }
    return get(user, "Roles", []).find(
        (r) =>
            r.Role === RoleTypes.AI_READ &&
            r.LocalRights.find((lr) => lr.FarmID === farm)
    );
});

export const makeHasPaymentsWriteRole = () =>
    createSelector([getUser], (user) => {
        return get(user, "Roles", []).find(
            (r) => r.Role === RoleTypes.PAYMENTS_WRITE
        );
    });

export const makeHasFirmwareWriteRole = () =>
    createSelector([getUser], (user) => {
        return !!get(user, "Roles", []).find(
            (r) => r.Role === RoleTypes.FIRMWARE_WRITE
        );
    });
